import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Search, Play, Pause, Pencil, Trash2, Loader2, Music2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { CatalogUploadDialog } from './CatalogUploadDialog';
import { ENERGIES, formatDuration } from '../../utils/catalogUpload';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

const EditDialog = ({ track, onClose, onSaved }) => {
  const [form, setForm] = useState(() => ({ ...track }));
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title.trim()) return toast.error('Title is required');
    setSaving(true);
    try {
      const body = {
        title: form.title, artist: form.artist, album: form.album, genre: form.genre,
        energy: form.energy || '', bpm: Number(form.bpm) || 0, language: form.language,
        year: Number(form.year) || 0, label: form.label, duration_ms: form.duration_ms || 0,
      };
      const r = await axios.put(`${API}/catalog/tracks/${track.id}`, body, { withCredentials: true });
      toast.success('Saved');
      onSaved(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const field = (k, label, placeholder) => (
    <div>
      <Label className="text-[11px] text-gray-400">{label}</Label>
      <Input value={form[k] ?? ''} placeholder={placeholder} onChange={(e) => set(k, e.target.value)} className="h-9 bg-black/50 border-white/10" data-testid={`edit-${k}`} />
    </div>
  );

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg bg-[#0a0a0a] border-white/10 text-white" data-testid="catalog-edit-dialog">
        <DialogHeader><DialogTitle>Edit song</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {field('title', 'Title')}
          {field('artist', 'Artist')}
          {field('album', 'Album')}
          {field('genre', 'Genre', 'pop, rock…')}
          <div>
            <Label className="text-[11px] text-gray-400">Energy</Label>
            <Select value={form.energy || undefined} onValueChange={(v) => set('energy', v)}>
              <SelectTrigger className="h-9 bg-black/50 border-white/10" data-testid="edit-energy"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>{ENERGIES.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {field('bpm', 'BPM')}
          {field('language', 'Language')}
          {field('year', 'Year')}
          {field('label', 'Label')}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#FF007F] hover:bg-[#CC0066]" data-testid="catalog-edit-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const CatalogSongsTab = () => {
  const [tracks, setTracks] = useState([]);
  const [genres, setGenres] = useState([]);
  const [q, setQ] = useState('');
  const [genre, setGenre] = useState('all');
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [playingId, setPlayingId] = useState(null);
  const audioRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (genre !== 'all') params.genre = genre;
      const [t, g] = await Promise.all([
        axios.get(`${API}/catalog/tracks`, { params, withCredentials: true }),
        axios.get(`${API}/catalog/genres`, { withCredentials: true }),
      ]);
      setTracks(t.data.tracks || []);
      setGenres(g.data.genres || []);
    } catch {
      toast.error('Failed to load catalog');
    } finally {
      setLoading(false);
    }
  }, [q, genre]);

  useEffect(() => {
    const id = setTimeout(load, 300);
    return () => clearTimeout(id);
  }, [load]);

  const togglePlay = (track) => {
    const el = audioRef.current;
    if (!el) return;
    if (playingId === track.id) {
      el.pause();
      setPlayingId(null);
      return;
    }
    el.src = `${BACKEND}${track.url}`;
    el.play().then(() => setPlayingId(track.id)).catch(() => toast.error('Preview failed'));
  };

  const del = async (track) => {
    if (!window.confirm(`Delete "${track.title}" from the catalog?`)) return;
    try {
      await axios.delete(`${API}/catalog/tracks/${track.id}`, { withCredentials: true });
      setTracks((prev) => prev.filter((t) => t.id !== track.id));
      toast.success('Deleted');
    } catch {
      toast.error('Delete failed');
    }
  };

  return (
    <div className="space-y-4" data-testid="catalog-songs-tab">
      <audio ref={audioRef} onEnded={() => setPlayingId(null)} className="hidden" />

      <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title, artist, album…" className="pl-9 h-10 bg-black/50 border-white/10" data-testid="catalog-search" />
        </div>
        <Select value={genre} onValueChange={setGenre}>
          <SelectTrigger className="w-full sm:w-44 h-10 bg-black/50 border-white/10" data-testid="catalog-genre-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All genres</SelectItem>
            {genres.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button onClick={() => setUploadOpen(true)} className="h-10 bg-[#FF007F] hover:bg-[#CC0066] gap-2" data-testid="catalog-upload-open">
          <Plus className="w-4 h-4" /> Upload songs
        </Button>
      </div>

      {loading ? (
        <div className="py-16 text-center text-gray-500"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
      ) : tracks.length === 0 ? (
        <div className="py-16 text-center" data-testid="catalog-empty">
          <Music2 className="w-10 h-10 text-gray-700 mx-auto mb-3" />
          <p className="text-gray-400">No songs yet. Upload your licensed catalog to get started.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-white/10" data-testid="catalog-tracks-table">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.03] text-gray-400 text-[11px] uppercase tracking-wider">
              <tr>
                <th className="text-left font-medium px-3 py-2.5 w-10" />
                <th className="text-left font-medium px-3 py-2.5">Title</th>
                <th className="text-left font-medium px-3 py-2.5">Artist</th>
                <th className="text-left font-medium px-3 py-2.5 hidden md:table-cell">Album</th>
                <th className="text-left font-medium px-3 py-2.5">Genre</th>
                <th className="text-left font-medium px-3 py-2.5 hidden lg:table-cell">Energy</th>
                <th className="text-left font-medium px-3 py-2.5 hidden lg:table-cell">BPM</th>
                <th className="text-left font-medium px-3 py-2.5 hidden xl:table-cell">Lang</th>
                <th className="text-left font-medium px-3 py-2.5 hidden xl:table-cell">Year</th>
                <th className="text-left font-medium px-3 py-2.5 hidden xl:table-cell">Label</th>
                <th className="text-right font-medium px-3 py-2.5">Time</th>
                <th className="text-right font-medium px-3 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {tracks.map((t) => (
                <tr key={t.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`catalog-track-${t.id}`}>
                  <td className="px-3 py-2">
                    <button type="button" onClick={() => togglePlay(t)} className="w-7 h-7 rounded-full bg-white/5 hover:bg-[#FF007F]/20 flex items-center justify-center" data-testid={`preview-${t.id}`}>
                      {playingId === t.id ? <Pause className="w-3.5 h-3.5 text-[#FF007F]" /> : <Play className="w-3.5 h-3.5 text-gray-300 ml-0.5" />}
                    </button>
                  </td>
                  <td className="px-3 py-2 text-white font-medium max-w-[220px] truncate">{t.title}</td>
                  <td className="px-3 py-2 text-gray-300">{t.artist || '—'}</td>
                  <td className="px-3 py-2 text-gray-400 hidden md:table-cell">{t.album || '—'}</td>
                  <td className="px-3 py-2">{t.genre ? <Badge variant="secondary" className="bg-white/5 text-gray-300 font-normal">{t.genre}</Badge> : '—'}</td>
                  <td className="px-3 py-2 text-gray-400 hidden lg:table-cell">{t.energy || '—'}</td>
                  <td className="px-3 py-2 text-gray-400 hidden lg:table-cell">{t.bpm || '—'}</td>
                  <td className="px-3 py-2 text-gray-400 hidden xl:table-cell">{t.language || '—'}</td>
                  <td className="px-3 py-2 text-gray-400 hidden xl:table-cell">{t.year || '—'}</td>
                  <td className="px-3 py-2 text-gray-400 hidden xl:table-cell max-w-[120px] truncate">{t.label || '—'}</td>
                  <td className="px-3 py-2 text-right text-gray-400 font-mono">{formatDuration(t.duration_ms)}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button type="button" onClick={() => setEditing(t)} className="text-gray-400 hover:text-white p-1.5" data-testid={`edit-${t.id}`}><Pencil className="w-4 h-4" /></button>
                    <button type="button" onClick={() => del(t)} className="text-gray-400 hover:text-red-400 p-1.5" data-testid={`delete-${t.id}`}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CatalogUploadDialog open={uploadOpen} onOpenChange={setUploadOpen} onUploaded={load} />
      {editing && <EditDialog track={editing} onClose={() => setEditing(null)} onSaved={(u) => { setTracks((prev) => prev.map((t) => (t.id === u.id ? u : t))); setEditing(null); }} />}
    </div>
  );
};
